import { useState, useEffect, useCallback } from "react";
import { getSupabase } from "@/lib/supabase";

interface Dealer {
  id: string;
  dealer_code: string;
  dealer_name: string;
  is_active: boolean;
  has_accounting: boolean;
  has_e_archive: boolean;
  has_e_invoice: boolean;
  created_at: string;
}

export default function AdminDashboardPage() {
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [totalSales, setTotalSales] = useState(0);
  const [totalProducts, setTotalProducts] = useState(0);
  const [openTickets, setOpenTickets] = useState(0);
  const [recentSales, setRecentSales] = useState<Array<{ id: string; total_amount: number; payment_method: string; created_at: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const supabase = getSupabase();

      const [dealersRes, salesRes, productsRes, ticketsRes, recentSalesRes] = await Promise.all([
        supabase.from("dealers").select("*").order("created_at", { ascending: false }),
        supabase.from("sales").select("total_amount"),
        supabase.from("products").select("id", { count: "exact", head: true }),
        supabase.from("support_tickets").select("id").eq("status", "open"),
        supabase.from("sales").select("id, total_amount, payment_method, created_at").order("created_at", { ascending: false }).limit(8),
      ]);

      if (dealersRes.error) throw dealersRes.error;
      if (salesRes.error) throw salesRes.error;
      if (ticketsRes.error) throw ticketsRes.error;

      setDealers(dealersRes.data || []);
      setTotalSales((salesRes.data || []).reduce((sum, s) => sum + Number(s.total_amount), 0));
      setTotalProducts(productsRes.count || 0);
      setOpenTickets((ticketsRes.data || []).length);
      setRecentSales(recentSalesRes.data || []);
    } catch (err) {
      setError("Veri yüklenirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const activeDealers = dealers.filter((d) => d.is_active).length;

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h4 className="text-lg font-semibold text-foreground-950"><a id="admin-dashboard">Admin Dashboard</a></h4>
        <p className="text-sm text-foreground-500 mt-0.5">Sistem genel durumu ve özet bilgiler</p>
      </div>

      {error ? (
        <div className="bg-accent-100 border border-accent-300 text-accent-900 rounded-xl px-4 py-3 mb-6 text-sm flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {error}
          <button onClick={fetchData} className="ml-auto text-accent-700 font-medium cursor-pointer">Tekrar Dene</button>
        </div>
      ) : null}

      {loading && !error ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center"><i className="ri-store-2-line text-primary-600"></i></div>
                <div><p className="text-xs text-foreground-400">Toplam Bayi</p><p className="text-lg font-bold text-foreground-950">{dealers.length}</p></div>
              </div>
              <p className="text-xs text-foreground-400">{activeDealers} aktif, {dealers.length - activeDealers} pasif</p>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-accent-100 rounded-lg flex items-center justify-center"><i className="ri-shopping-cart-2-line text-accent-600"></i></div>
                <div><p className="text-xs text-foreground-400">Toplam Satış</p><p className="text-lg font-bold text-foreground-950">${totalSales.toLocaleString("en-US", { minimumFractionDigits: 2 })}</p></div>
              </div>
              <p className="text-xs text-foreground-400">Tüm bayiler dahil</p>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-secondary-100 rounded-lg flex items-center justify-center"><i className="ri-customer-service-2-line text-secondary-600"></i></div>
                <div><p className="text-xs text-foreground-400">Açık Talepler</p><p className="text-lg font-bold text-foreground-950">{openTickets}</p></div>
              </div>
              <p className="text-xs text-foreground-400">Destek bekleyen talepler</p>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center"><i className="ri-box-3-line text-primary-600"></i></div>
                <div><p className="text-xs text-foreground-400">Toplam Ürün</p><p className="text-lg font-bold text-foreground-950">{totalProducts}</p></div>
              </div>
              <p className="text-xs text-foreground-400">Tüm bayilerdeki ürünler</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-background-50 border border-background-200/70 rounded-xl">
              <div className="px-4 py-3 border-b border-background-200/70"><h4 className="font-medium text-foreground-900 text-sm"><a id="recent-sales">Son Satışlar</a></h4></div>
              <div className="p-4 space-y-2 max-h-72 overflow-y-auto">
                {recentSales.length === 0 ? (
                  <p className="text-sm text-foreground-400 text-center py-4">Henüz satış kaydı yok.</p>
                ) : (
                  recentSales.map((s) => (
                    <div key={s.id} className="flex items-center justify-between py-2 border-b border-background-200/70 last:border-0">
                      <div>
                        <p className="text-sm font-medium text-foreground-900">
                          {s.payment_method === "nakit" ? "Nakit" : s.payment_method === "pos" ? "POS" : s.payment_method === "iban" ? "IBAN" : "Cari"} Satış
                        </p>
                        <p className="text-xs text-foreground-400">{new Date(s.created_at).toLocaleDateString("tr-TR")}</p>
                      </div>
                      <span className="text-sm font-semibold text-primary-600">${Number(s.total_amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="bg-background-50 border border-background-200/70 rounded-xl">
              <div className="px-4 py-3 border-b border-background-200/70"><h4 className="font-medium text-foreground-900 text-sm"><a id="active-dealers">Bayi Durumu</a></h4></div>
              <div className="p-4 space-y-2 max-h-72 overflow-y-auto">
                {dealers.length === 0 ? (
                  <p className="text-sm text-foreground-400 text-center py-4">Henüz bayi kaydı yok.</p>
                ) : (
                  dealers.map((d) => (
                    <div key={d.id} className="flex items-center justify-between py-2 border-b border-background-200/70 last:border-0">
                      <div>
                        <p className="text-sm font-medium text-foreground-900">{d.dealer_name}</p>
                        <p className="text-xs text-foreground-400">{d.dealer_code}</p>
                      </div>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${d.is_active ? "bg-primary-100 text-primary-700" : "bg-foreground-100 text-foreground-500"}`}>
                        {d.is_active ? "Aktif" : "Pasif"}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}